import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import { Users, UserPlus, Sparkles, FileUp, Search as SearchIcon, Loader2, Trash2, Shield, Mail, AlertTriangle, MessageCircle, CheckCircle2, Eye, Globe, MapPin, FileText } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="label-mono text-slate-500">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="font-display text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [support, setSupport] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard"); return; }
  }, [user, navigate]);

  const load = async () => {
    try {
      const [s, u, sup] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/support")]);
      setStats(s.data); setUsers(u.data.users); setSupport(sup.data.messages);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed to load admin data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadAnalytics = async (d) => {
    try { const { data } = await api.get(`/admin/analytics?days=${d}`); setAnalytics(data); }
    catch { /* silent */ }
  };
  useEffect(() => { loadAnalytics(range); }, [range]);

  const resolveMsg = async (id) => {
    try { await api.post(`/admin/support/${id}/resolve`); load(); } catch { toast.error("Failed"); }
  };
  const deleteMsg = async (id) => {
    try { await api.delete(`/admin/support/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const doDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${confirm.user_id}`);
      toast.success(`Deleted ${confirm.email}`);
      setConfirm(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setDeleting(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  if (loading) return <AppShell><div className="flex items-center gap-3 text-slate-400 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading admin…</div></AppShell>;

  return (
    <AppShell>
      <div className="space-y-6 lg:space-y-8 animate-fade-up">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600/15 border border-violet-500/25 flex items-center justify-center"><Shield className="w-5 h-5 text-violet-400" /></div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Admin Console</h1>
            <p className="text-slate-500 text-sm font-mono-niq">Platform users & analytics</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total users" value={stats.total_users} sub={`${stats.admins} admin${stats.admins !== 1 ? "s" : ""}`} accent="bg-blue-600/15 text-blue-400" />
          <StatCard icon={Sparkles} label="Pro subscribers" value={stats.pro_users} sub={`${stats.free_users} on free`} accent="bg-emerald-600/15 text-emerald-400" />
          <StatCard icon={UserPlus} label="New (7 days)" value={stats.new_users_7d} accent="bg-violet-600/15 text-violet-400" />
          <StatCard icon={FileUp} label="Uploads" value={stats.total_uploads} sub={`${stats.total_searches} searches`} accent="bg-amber-600/15 text-amber-400" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
            <h3 className="font-display text-lg font-semibold mb-1">Signups</h3>
            <p className="text-xs text-slate-500 mb-5 font-mono-niq">New accounts · last 14 days</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.signups_by_day} margin={{ left: -20, right: 10 }}>
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(59,130,246,0.08)" }} contentStyle={{ background: "#1A2234", border: "1px solid #27334D", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {stats.signups_by_day.map((_, i) => <Cell key={i} fill="#3B82F6" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Engagement</h3>
            <div className="space-y-4">
              <div><div className="label-mono text-slate-500 mb-1">Contacts enriched</div><div className="font-display text-2xl font-bold">{stats.total_contacts_enriched.toLocaleString()}</div></div>
              <div className="pt-3 border-t border-[#1E2A3F]">
                <div className="label-mono text-slate-500 mb-2">Sign-in method</div>
                <div className="flex items-center justify-between text-sm mb-1"><span className="text-slate-300">Email</span><span className="font-mono-niq text-slate-500">{stats.providers.email || 0}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-300">Google</span><span className="font-mono-niq text-slate-500">{stats.providers.google || 0}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Website visitors ---------- */}
        <div className="space-y-4 lg:space-y-6" data-testid="admin-analytics">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-cyan-600/15 border border-cyan-500/25 flex items-center justify-center"><Eye className="w-4.5 h-4.5 text-cyan-400" /></div>
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight">Website visitors</h2>
                <p className="text-slate-500 text-xs font-mono-niq">Traffic, pages and countries</p>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-[#111726] border border-[#27334D] rounded-lg p-1">
              {[7, 30, 90].map((d) => (
                <button key={d} onClick={() => setRange(d)} data-testid={`analytics-range-${d}`} className={`px-3 py-1.5 rounded-md text-xs font-mono-niq transition-colors ${range === d ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>{d}d</button>
              ))}
            </div>
          </div>

          {!analytics ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading visitor data…</div>
          ) : analytics.total_visits === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#27334D] bg-[#111726] px-5 py-10 text-center">
              <Eye className="w-6 h-6 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No visits recorded yet in the last {analytics.days} days.</p>
              <p className="text-xs text-slate-600 mt-1 font-mono-niq">Visits are tracked automatically as people browse the site.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard icon={Eye} label="Total visits" value={analytics.total_visits.toLocaleString()} sub={`last ${analytics.days} days`} accent="bg-cyan-600/15 text-cyan-400" />
                <StatCard icon={Users} label="Unique visitors" value={analytics.unique_visitors.toLocaleString()} accent="bg-blue-600/15 text-blue-400" />
                <StatCard icon={UserPlus} label="Visits today" value={analytics.visits_today.toLocaleString()} accent="bg-emerald-600/15 text-emerald-400" />
              </div>

              <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
                <h3 className="font-display text-lg font-semibold mb-1">Traffic</h3>
                <p className="text-xs text-slate-500 mb-5 font-mono-niq">Visits per day · last {analytics.days} days</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={analytics.by_day} margin={{ left: -20, right: 10 }}>
                    <defs>
                      <linearGradient id="vg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(analytics.by_day.length / 12))} />
                    <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ stroke: "rgba(34,211,238,0.3)" }} contentStyle={{ background: "#1A2234", border: "1px solid #27334D", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                    <Area type="monotone" dataKey="count" stroke="#22D3EE" strokeWidth={2} fill="url(#vg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden" data-testid="analytics-top-countries">
                  <div className="px-5 py-4 border-b border-[#1E2A3F] flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /><h3 className="font-display text-base font-semibold">Top countries</h3></div>
                  <div className="divide-y divide-[#1E2A3F]">
                    {analytics.top_countries.map((c) => (
                      <div key={c.name} className="px-5 py-2.5 flex items-center justify-between text-sm">
                        <span className="text-slate-300 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-500" /> {c.name}</span>
                        <span className="font-mono-niq text-slate-400">{c.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden" data-testid="analytics-top-pages">
                  <div className="px-5 py-4 border-b border-[#1E2A3F] flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" /><h3 className="font-display text-base font-semibold">Top pages</h3></div>
                  <div className="divide-y divide-[#1E2A3F]">
                    {analytics.top_pages.map((p) => (
                      <div key={p.name} className="px-5 py-2.5 flex items-center justify-between text-sm">
                        <span className="text-slate-300 font-mono-niq truncate max-w-[70%]">{p.name}</span>
                        <span className="font-mono-niq text-slate-400">{p.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden" data-testid="analytics-recent">
                <div className="px-5 py-4 border-b border-[#1E2A3F] flex items-center justify-between">
                  <h3 className="font-display text-base font-semibold">Recent visits</h3>
                  <span className="label-mono text-slate-500">latest {analytics.recent.length}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#1E2A3F] text-left">
                        {["Page", "Country", "City", "When"].map((h) => <th key={h} className="px-4 py-3 label-mono text-slate-500 font-normal whitespace-nowrap">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent.map((v, i) => (
                        <tr key={i} className="border-b border-[#1E2A3F] hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-2.5 font-mono-niq text-slate-300 whitespace-nowrap">{v.path}</td>
                          <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{v.country || "Unknown"}</td>
                          <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{v.city || "—"}</td>
                          <td className="px-4 py-2.5 text-slate-500 font-mono-niq text-xs whitespace-nowrap">{v.created_at ? new Date(v.created_at).toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden" data-testid="admin-users-table">
          <div className="px-5 py-4 border-b border-[#1E2A3F] flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Users</h3>
            <span className="label-mono text-slate-500">{users.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A3F] text-left">
                  {["Name", "Email", "Plan", "Method", "Contacts", "Joined", ""].map((h) => <th key={h} className="px-4 py-3 label-mono text-slate-500 font-normal whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-[#1E2A3F] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-xs font-semibold text-blue-300">{(u.name || u.email)[0]?.toUpperCase()}</div>
                        <span className="font-medium">{u.name || "—"}</span>
                        {u.role === "admin" && <span className="text-[9px] font-mono-niq uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/25">Admin</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-300"><span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-500" /> {u.email}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.subscription_status === "active"
                        ? <span className="text-[10px] font-mono-niq uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25">Pro</span>
                        : <span className="text-[10px] font-mono-niq uppercase px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 border border-slate-500/25">Free</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-400 capitalize">{u.auth_provider}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-400 font-mono-niq text-xs">{u.contacts.toLocaleString()}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 font-mono-niq text-xs">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {u.role === "admin" ? (
                        <span className="text-xs text-slate-600">protected</span>
                      ) : (
                        <button onClick={() => setConfirm(u)} data-testid={`admin-delete-user-${u.user_id}`} className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden" data-testid="admin-support-inbox">
          <div className="px-5 py-4 border-b border-[#1E2A3F] flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <MessageCircle className="w-4.5 h-4.5 text-blue-400" />
              <h3 className="font-display text-lg font-semibold">Support inbox</h3>
            </div>
            <span className="label-mono text-slate-500">{support.filter((m) => m.status === "new").length} new · {support.length} total</span>
          </div>
          {support.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">No support messages yet. Messages from the website chat land here.</div>
          ) : (
            <div className="divide-y divide-[#1E2A3F]">
              {support.map((m) => (
                <div key={m.id} data-testid={`support-msg-${m.id}`} className={`px-5 py-4 ${m.status === "new" ? "" : "opacity-60"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-sm">{m.name}</span>
                        <a href={`mailto:${m.email}`} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</a>
                        {m.status === "new"
                          ? <span className="text-[9px] font-mono-niq uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/25">New</span>
                          : <span className="text-[9px] font-mono-niq uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/25">Resolved</span>}
                        <span className="text-[10px] text-slate-600 font-mono-niq">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                      </div>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap break-words">{m.message}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {m.status === "new" && (
                        <button onClick={() => resolveMsg(m.id)} data-testid={`support-resolve-${m.id}`} title="Mark resolved" className="p-2 rounded-lg text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => deleteMsg(m.id)} data-testid={`support-delete-${m.id}`} title="Delete" className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={() => !deleting && setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl border border-[#27334D] bg-[#111726] p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/25 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
              <h3 className="font-display text-lg font-semibold">Delete user?</h3>
            </div>
            <p className="text-sm text-slate-400 mb-1">This permanently removes <span className="text-slate-200 font-medium">{confirm.email}</span> and all their data:</p>
            <p className="text-xs text-slate-500 font-mono-niq mb-6">{confirm.uploads} upload(s) · {confirm.contacts.toLocaleString()} contacts · {confirm.searches} search(es)</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirm(null)} disabled={deleting} className="px-4 py-2.5 rounded-lg border border-[#27334D] hover:border-slate-500 text-sm transition-colors">Cancel</button>
              <button onClick={doDelete} disabled={deleting} data-testid="admin-confirm-delete" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-sm font-medium transition-colors disabled:opacity-60">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
